import React, { useMemo, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "./api"; // ✅ shared axios client
import {
  Pencil,
  Trash2,
  Download,
  X,
  Paperclip,
  MessageSquareText,
  Image as ImageIcon,
} from "lucide-react";

function WorkOrdersList() {
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);

  // ✅ Attachments counts map: { "12": { comments: 3, images: 2 }, ... }
  const [attachmentsCounts, setAttachmentsCounts] = useState({});

  // ✅ Modal state
  const [attachmentsOpen, setAttachmentsOpen] = useState(false);
  const [activeWoId, setActiveWoId] = useState(null);

  const [comments, setComments] = useState([]);
  const [images, setImages] = useState([]);

  const [attachmentsLoading, setAttachmentsLoading] = useState(false);

  const [newComment, setNewComment] = useState("");
  const [addingComment, setAddingComment] = useState(false);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  // ✅ Filters (frontend-only)
  // ✅ Status default = "open" (so you see open only by default)
  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
    line: "ALL",
    machine: "ALL",
    type: "ALL",
    assignee: "ALL",
    status: "open", // ✅ default: show only open
  });

  useEffect(() => {
    fetchWorkOrders();
    fetchAttachmentsCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchWorkOrders = () => {
    api
      .get("/api/work-orders")
      .then((res) => setWorkOrders(res.data || []))
      .catch((err) => console.error("Error fetching work orders", err));
  };

  const fetchAttachmentsCounts = () => {
    api
      .get("/api/work-orders/attachments-counts")
      .then((res) => setAttachmentsCounts(res.data || {}))
      .catch((err) =>
        console.error("Error fetching attachments counts", err)
      );
  };

  // ✅ refresh counts (safe)
  const refreshCounts = () => fetchAttachmentsCounts();

  const getAssignee = (wo) => wo.assignee || wo.assigned_to || wo.worker || "";

  // ---------------------------
  // ✅ Date formatting (display + filtering) WITHOUT timezone shift
  // ---------------------------
  const pad2 = (n) => String(n).padStart(2, "0");

  // ✅ Parse dates safely:
  // - If value is "YYYY-MM-DD" (date-only), treat it as LOCAL date (no UTC shift)
  // - Otherwise, let Date parse it normally (timestamps / ISO strings with time)
  const parseDateSmart = (v) => {
    if (!v) return null;

    // if it's already a Date
    if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;

    const s = String(v).trim();

    // date-only: YYYY-MM-DD
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const y = Number(m[1]);
      const mo = Number(m[2]) - 1; // 0-based
      const d = Number(m[3]);
      const local = new Date(y, mo, d); // ✅ local midnight (no timezone conversion)
      return Number.isNaN(local.getTime()) ? null : local;
    }

    // default parsing for ISO w/ time, timestamps, etc.
    const dt = new Date(s);
    return Number.isNaN(dt.getTime()) ? null : dt;
  };

  const formatDateMMDDYY = (v) => {
    const d = parseDateSmart(v);
    if (!d) return v ? String(v) : "";

    const mm = pad2(d.getMonth() + 1);
    const dd = pad2(d.getDate());
    const yy = String(d.getFullYear()).slice(-2);
    return `${mm}-${dd}-${yy}`;
  };

  const toDateSafe = (v) => parseDateSmart(v);

  // ✅ Lead time could be a date OR text/number.
  // If it parses like a date -> format as MM-DD-YY, else return as-is.
  const formatLeadTimeSmart = (v) => {
    if (v === null || v === undefined || v === "") return "";
    const s = String(v).trim();

    // if it's clearly a number or duration text, keep it
    if (/^\d+(\.\d+)?$/.test(s)) return s;
    if (
      /(day|days|hour|hours|hr|hrs|min|mins|week|weeks|month|months|year|years)/i.test(
        s
      )
    )
      return s;

    const d = parseDateSmart(s);
    if (!d) return s;

    return formatDateMMDDYY(d);
  };

  // ---------------------------
  // ✅ Normalizers
  // ---------------------------
  const normalize = (v) => String(v || "").trim();
  const normalizeStatus = (v) => String(v || "").trim().toLowerCase();

  const uniqueSorted = (arr) =>
    Array.from(new Set(arr.filter(Boolean))).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );

  const updateStatus = async (woId, newStatus) => {
    try {
      await api.put(`/api/work-orders/${woId}`, { status: newStatus });
      fetchWorkOrders();
      refreshCounts();
    } catch (err) {
      console.error("Error updating status", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        "Unknown error";
      alert(`❌ Failed to update status: ${msg}`);
    }
  };

  const deleteWorkOrder = async (woId) => {
    if (!window.confirm("Delete this work order?")) return;

    try {
      await api.delete(`/api/work-orders/${woId}`);
      fetchWorkOrders();
      refreshCounts();
    } catch (err) {
      console.error("Error deleting work order", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        "Unknown error";
      alert(`❌ Failed to delete work order: ${msg}`);
    }
  };

  // ✅ Dropdown options generated from data (no backend change)
  const options = useMemo(() => {
    const lines = uniqueSorted(workOrders.map((wo) => normalize(wo.line)));
    const machines = uniqueSorted(workOrders.map((wo) => normalize(wo.machine)));
    const types = uniqueSorted(workOrders.map((wo) => normalize(wo.type)));
    const assignees = uniqueSorted(
      workOrders.map((wo) => normalize(getAssignee(wo)))
    );
    return { lines, machines, types, assignees };
  }, [workOrders]);

  // ✅ Apply filters (frontend-only)
  const filteredWorkOrders = useMemo(() => {
    const from = filters.dateFrom ? toDateSafe(filters.dateFrom) : null;
    const to = filters.dateTo ? toDateSafe(filters.dateTo) : null;

    return (workOrders || []).filter((wo) => {
      if (filters.status !== "ALL") {
        const st = normalizeStatus(wo.status || "open");
        if (st !== normalizeStatus(filters.status)) return false;
      }

      if (from || to) {
        const woDate = toDateSafe(wo.date);
        if (!woDate) return false;

        if (from && woDate < from) return false;

        if (to) {
          const endOfTo = new Date(to);
          endOfTo.setHours(23, 59, 59, 999);
          if (woDate > endOfTo) return false;
        }
      }

      if (filters.line !== "ALL") {
        if (normalize(wo.line) !== filters.line) return false;
      }

      if (filters.machine !== "ALL") {
        if (normalize(wo.machine) !== filters.machine) return false;
      }

      if (filters.type !== "ALL") {
        if (normalize(wo.type) !== filters.type) return false;
      }

      if (filters.assignee !== "ALL") {
        if (normalize(getAssignee(wo)) !== filters.assignee) return false;
      }

      return true;
    });
  }, [workOrders, filters]);

  const clearFilters = () => {
    setFilters({
      dateFrom: "",
      dateTo: "",
      line: "ALL",
      machine: "ALL",
      type: "ALL",
      assignee: "ALL",
      status: "open",
    });
  };

  // ---------------------------
  // ✅ Export CSV (include attachments counts columns)
  // ---------------------------
  const getCountsForWo = (woId) => {
    const key = String(woId);
    const entry = attachmentsCounts?.[key] || {};
    return {
      comments: Number(entry.comments || 0),
      images: Number(entry.images || 0),
    };
  };

  const handleExport = () => {
    const listToExport = filteredWorkOrders;
    if (!listToExport.length) {
      alert("No work orders to export");
      return;
    }

    const headers = [
      "WO ID",
      "Date (MM-DD-YY)",
      "Line",
      "Machine",
      "Task",
      "Type",
      "Status",
      "Assignee",
      "Lead Time (MM-DD-YY or original)",
      "Scheduled Date (MM-DD-YY)",
      "Comments Count",
      "Images Count",
    ];

    const rows = listToExport.map((wo) => {
      const c = getCountsForWo(wo.id);
      return [
        wo.id || "",
        formatDateMMDDYY(wo.date) || "",
        wo.line || "",
        wo.machine || "",
        (wo.task || "").replace(/\n/g, " "),
        wo.type || "",
        wo.status || "",
        getAssignee(wo) || "",
        formatLeadTimeSmart(wo.lead_time) || "",
        formatDateMMDDYY(wo.scheduled_date) || "",
        c.comments,
        c.images,
      ];
    });

    const csvLines = [headers, ...rows]
      .map((row) =>
        row
          .map((field) => {
            const value = String(field ?? "");
            return `"${value.replace(/"/g, '""')}"`;
          })
          .join(",")
      )
      .join("\r\n");

    const blob = new Blob([csvLines], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "work_orders.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const FilterSelect = ({ value, onChange, optionsList, placeholder }) => (
    <select
      value={value}
      onChange={onChange}
      style={{
        width: "100%",
        padding: "8px 10px",
        borderRadius: 10,
        border: "1px solid rgba(148,163,184,0.25)",
        background: "rgba(255,255,255,0.03)",
        color: "inherit",
        outline: "none",
        fontSize: 13,
      }}
    >
      <option value="ALL">{placeholder}</option>
      {optionsList.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  );

  const FilterSelectFixed = ({ value, onChange, optionsList }) => (
    <select
      value={value}
      onChange={onChange}
      style={{
        width: "100%",
        padding: "8px 10px",
        borderRadius: 10,
        border: "1px solid rgba(148,163,184,0.25)",
        background: "rgba(255,255,255,0.03)",
        color: "inherit",
        outline: "none",
        fontSize: 13,
      }}
    >
      {optionsList.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );

  // ---------------------------
  // ✅ Attachments modal helpers
  // ---------------------------
  const joinUrl = (base, path) => {
    const b = String(base || "").replace(/\/+$/, "");
    const p = String(path || "").replace(/^\/+/, "");
    return b ? `${b}/${p}` : `/${p}`;
  };

  const openAttachments = async (woId) => {
    setActiveWoId(woId);
    setAttachmentsOpen(true);
    setAttachmentsLoading(true);
    setComments([]);
    setImages([]);
    setNewComment("");
    setSelectedFiles([]);

    try {
      const [commentsRes, imagesRes] = await Promise.all([
        api.get(`/api/work-orders/${woId}/comments`),
        api.get(`/api/work-orders/${woId}/images`),
      ]);
      setComments(commentsRes.data || []);
      setImages(imagesRes.data || []);
      refreshCounts();
    } catch (err) {
      console.error("Error loading attachments", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        "Unknown error";
      alert(`❌ Failed to load attachments: ${msg}`);
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const closeAttachments = () => {
    setAttachmentsOpen(false);
    setActiveWoId(null);
    setComments([]);
    setImages([]);
    setNewComment("");
    setSelectedFiles([]);
    setAttachmentsLoading(false);
    setAddingComment(false);
    setUploadingImages(false);
  };

  const reloadAttachments = async () => {
    if (!activeWoId) return;
    setAttachmentsLoading(true);
    try {
      const [commentsRes, imagesRes] = await Promise.all([
        api.get(`/api/work-orders/${activeWoId}/comments`),
        api.get(`/api/work-orders/${activeWoId}/images`),
      ]);
      setComments(commentsRes.data || []);
      setImages(imagesRes.data || []);
      refreshCounts();
    } catch (err) {
      console.error("Error reloading attachments", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        "Unknown error";
      alert(`❌ Failed to refresh attachments: ${msg}`);
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const addComment = async () => {
    if (!activeWoId) return;
    const text = String(newComment || "").trim();
    if (!text) return;

    setAddingComment(true);
    try {
      await api.post(`/api/work-orders/${activeWoId}/comments`, {
        comment: text,
      });
      setNewComment("");
      await reloadAttachments();
    } catch (err) {
      console.error("Error adding comment", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        "Unknown error";
      alert(`❌ Failed to add comment: ${msg}`);
    } finally {
      setAddingComment(false);
    }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/api/work-orders/comments/${commentId}`);
      await reloadAttachments();
    } catch (err) {
      console.error("Error deleting comment", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        "Unknown error";
      alert(`❌ Failed to delete comment: ${msg}`);
    }
  };

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(files);
  };

  const uploadImages = async () => {
    if (!activeWoId) return;
    if (!selectedFiles || selectedFiles.length === 0) {
      alert("Choose images first");
      return;
    }

    setUploadingImages(true);
    try {
      const fd = new FormData();
      selectedFiles.forEach((f) => fd.append("files", f));

      await api.post(`/api/work-orders/${activeWoId}/images`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSelectedFiles([]);
      await reloadAttachments();
    } catch (err) {
      console.error("Error uploading images", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        "Unknown error";
      alert(`❌ Failed to upload images: ${msg}`);
    } finally {
      setUploadingImages(false);
    }
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await api.delete(`/api/work-orders/images/${imageId}`);
      await reloadAttachments();
    } catch (err) {
      console.error("Error deleting image", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        "Unknown error";
      alert(`❌ Failed to delete image: ${msg}`);
    }
  };

  const viewImage = (imageId) => {
    const base = api?.defaults?.baseURL || "";
    const url = joinUrl(base, `/api/work-orders/images/${imageId}/view`);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // ✅ FIXED: DO NOT use className="icon-btn" here (it clamps width/height)
  const AttachmentsCell = ({ woId }) => {
    const c = getCountsForWo(woId);

    const badgeStyle = {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 8px",
      borderRadius: 999,
      border: "1px solid rgba(148,163,184,0.22)",
      background: "rgba(255,255,255,0.03)",
      fontSize: 12,
      fontWeight: 700,
      lineHeight: 1,
      whiteSpace: "nowrap",
    };

    return (
      <button
        type="button"
        onClick={() => openAttachments(woId)}
        title="Open attachments"
        style={{
          // 👇 Modern CMMS pill button (safe from .icon-btn clamp)
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 10,
          padding: "6px 10px",
          borderRadius: 12,
          border: "1px solid rgba(148,163,184,0.25)",
          background: "rgba(255,255,255,0.03)",
          color: "inherit",
          cursor: "pointer",
          whiteSpace: "nowrap",
          minWidth: 132,
          height: 34,
          overflow: "visible",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = "rgba(148,163,184,0.38)";
          e.currentTarget.style.background = "rgba(255,255,255,0.05)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "rgba(148,163,184,0.25)";
          e.currentTarget.style.background = "rgba(255,255,255,0.03)";
        }}
      >
        <Paperclip size={16} />

        <span style={badgeStyle}>
          <MessageSquareText size={14} />
          {c.comments}
        </span>

        <span style={badgeStyle}>
          <ImageIcon size={14} />
          {c.images}
        </span>
      </button>
    );
  };

  const activeWo = useMemo(() => {
    if (!activeWoId) return null;
    return (workOrders || []).find((w) => String(w.id) === String(activeWoId));
  }, [activeWoId, workOrders]);

  return (
    <div className="main-content">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h1 style={{ margin: 0 }}>📋 Work Orders</h1>
          <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 6 }}>
            Showing <strong>{filteredWorkOrders.length}</strong> of{" "}
            <strong>{workOrders.length}</strong>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button type="button" className="add-button" onClick={handleExport}>
            <Download size={16} />
            Export CSV
          </button>

          <Link to="/add-work-order" className="add-button">
            + New Work Order
          </Link>
        </div>
      </div>

      {/* ✅ Filters bar */}
      <div className="card" style={{ marginBottom: 12, padding: 12 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, minmax(150px, 1fr))",
            gap: 10,
            alignItems: "end",
          }}
        >
          {/* ✅ Status filter */}
          <div>
            <label style={{ fontSize: 12, color: "#9ca3af" }}>Status</label>
            <FilterSelectFixed
              value={filters.status}
              onChange={(e) =>
                setFilters((p) => ({ ...p, status: e.target.value }))
              }
              optionsList={[
                { label: "Open only", value: "open" },
                { label: "All statuses", value: "ALL" },
                { label: "Completed", value: "completed" },
                { label: "Not Completed", value: "not completed" },
                { label: "Reopened", value: "reopened" },
                { label: "Closed", value: "closed" },
                { label: "Project", value: "project" },
              ]}
            />
          </div>

          {/* Date from */}
          <div>
            <label style={{ fontSize: 12, color: "#9ca3af" }}>Date (From)</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) =>
                setFilters((p) => ({ ...p, dateFrom: e.target.value }))
              }
              style={{
                width: "100%",
                padding: "8px 10px",
                borderRadius: 10,
                border: "1px solid rgba(148,163,184,0.25)",
                background: "rgba(255,255,255,0.03)",
                color: "inherit",
                outline: "none",
                fontSize: 13,
              }}
            />
          </div>

          {/* Date to */}
          <div>
            <label style={{ fontSize: 12, color: "#9ca3af" }}>Date (To)</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) =>
                setFilters((p) => ({ ...p, dateTo: e.target.value }))
              }
              style={{
                width: "100%",
                padding: "8px 10px",
                borderRadius: 10,
                border: "1px solid rgba(148,163,184,0.25)",
                background: "rgba(255,255,255,0.03)",
                color: "inherit",
                outline: "none",
                fontSize: 13,
              }}
            />
          </div>

          {/* Line */}
          <div>
            <label style={{ fontSize: 12, color: "#9ca3af" }}>Line</label>
            <FilterSelect
              value={filters.line}
              onChange={(e) =>
                setFilters((p) => ({ ...p, line: e.target.value }))
              }
              optionsList={options.lines}
              placeholder="All Lines"
            />
          </div>

          {/* Machine */}
          <div>
            <label style={{ fontSize: 12, color: "#9ca3af" }}>Machine</label>
            <FilterSelect
              value={filters.machine}
              onChange={(e) =>
                setFilters((p) => ({ ...p, machine: e.target.value }))
              }
              optionsList={options.machines}
              placeholder="All Machines"
            />
          </div>

          {/* Type */}
          <div>
            <label style={{ fontSize: 12, color: "#9ca3af" }}>Type</label>
            <FilterSelect
              value={filters.type}
              onChange={(e) =>
                setFilters((p) => ({ ...p, type: e.target.value }))
              }
              optionsList={options.types}
              placeholder="All Types"
            />
          </div>

          {/* Assignee */}
          <div>
            <label style={{ fontSize: 12, color: "#9ca3af" }}>Assignee</label>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <FilterSelect
                  value={filters.assignee}
                  onChange={(e) =>
                    setFilters((p) => ({ ...p, assignee: e.target.value }))
                  }
                  optionsList={options.assignees}
                  placeholder="All Assignees"
                />
              </div>

              <button
                type="button"
                className="icon-btn ghost"
                onClick={clearFilters}
                title="Clear filters"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="table-wrapper">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>WO ID</th>
                <th>Date</th>
                <th>Line</th>
                <th>Machine</th>
                <th>Task</th>
                <th>Type</th>
                <th>Status</th>
                <th>Assignee</th>
                <th>Lead Time</th>
                <th>Scheduled Date</th>
                <th style={{ minWidth: 160, width: 160, whiteSpace: "nowrap" }}>
                  Attachments
                </th>
                <th style={{ width: 120 }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredWorkOrders.map((wo) => (
                <tr key={wo.id}>
                  <td>{wo.id}</td>

                  <td style={{ whiteSpace: "nowrap" }}>
                    {formatDateMMDDYY(wo.date)}
                  </td>

                  <td>{wo.line}</td>
                  <td>{wo.machine}</td>

                  <td style={{ maxWidth: 420, whiteSpace: "pre-wrap" }}>
                    {wo.task}
                  </td>

                  <td>{wo.type}</td>

                  <td>
                    <select
                      value={wo.status || "open"}
                      onChange={(e) => updateStatus(wo.id, e.target.value)}
                    >
                      <option value="open">Open</option>
                      <option value="completed">Completed</option>
                      <option value="not completed">Not Completed</option>
                      <option value="reopened">Reopened</option>
                      <option value="closed">Closed</option>
                      <option value="project">Project</option>
                    </select>
                  </td>

                  <td>{getAssignee(wo)}</td>

                  <td style={{ whiteSpace: "nowrap" }}>
                    {formatLeadTimeSmart(wo.lead_time)}
                  </td>

                  <td style={{ whiteSpace: "nowrap" }}>
                    {formatDateMMDDYY(wo.scheduled_date)}
                  </td>

                  {/* ✅ Attachments column (centered, no clamping) */}
                  <td
                    style={{
                      whiteSpace: "nowrap",
                      minWidth: 160,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <AttachmentsCell woId={wo.id} />
                    </div>
                  </td>

                  {/* ✅ icon actions */}
                  <td style={{ whiteSpace: "nowrap" }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <button
                        type="button"
                        className="icon-btn"
                        onClick={() => navigate(`/edit-work-order/${wo.id}`)}
                        title="Edit"
                      >
                        <Pencil size={16} />
                      </button>

                      <button
                        type="button"
                        className="icon-btn danger"
                        onClick={() => deleteWorkOrder(wo.id)}
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {filteredWorkOrders.length === 0 ? (
                <tr>
                  <td colSpan="12" style={{ padding: 14, color: "#9ca3af" }}>
                    No work orders match your filters.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {/* ✅ Attachments Modal */}
      {attachmentsOpen ? (
        <div
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeAttachments();
          }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            zIndex: 9999,
          }}
        >
          <div
            className="card"
            style={{
              width: "min(980px, 96vw)",
              maxHeight: "88vh",
              overflow: "hidden",
              padding: 0,
            }}
          >
            {/* Header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: 14,
                borderBottom: "1px solid rgba(148,163,184,0.18)",
                background: "rgba(255,255,255,0.02)",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Paperclip size={18} />
                  <div style={{ fontWeight: 700 }}>
                    Attachments — WO #{activeWoId}
                  </div>
                </div>
                <div style={{ fontSize: 12, color: "#9ca3af" }}>
                  {activeWo
                    ? `${activeWo.line || ""} • ${activeWo.machine || ""} • ${
                        activeWo.type || ""
                      }`
                    : " "}
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button
                  type="button"
                  className="icon-btn ghost"
                  onClick={reloadAttachments}
                  title="Refresh"
                  disabled={attachmentsLoading}
                >
                  <Download size={16} />
                </button>

                <button
                  type="button"
                  className="icon-btn ghost"
                  onClick={closeAttachments}
                  title="Close"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Body */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
                padding: 14,
                overflow: "auto",
                maxHeight: "calc(88vh - 64px)",
              }}
            >
              {/* Comments */}
              <div
                className="card"
                style={{
                  padding: 12,
                  border: "1px solid rgba(148,163,184,0.16)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 10,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <MessageSquareText size={16} />
                    <div style={{ fontWeight: 700 }}>
                      Comments{" "}
                      <span style={{ color: "#9ca3af", fontWeight: 600 }}>
                        ({comments.length})
                      </span>
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Write a comment…"
                    rows={2}
                    style={{
                      flex: 1,
                      padding: "10px 12px",
                      borderRadius: 12,
                      border: "1px solid rgba(148,163,184,0.25)",
                      background: "rgba(255,255,255,0.03)",
                      color: "inherit",
                      outline: "none",
                      fontSize: 13,
                      resize: "vertical",
                      minHeight: 44,
                    }}
                    disabled={attachmentsLoading || addingComment}
                  />

                  <button
                    type="button"
                    className="add-button"
                    onClick={addComment}
                    disabled={attachmentsLoading || addingComment}
                    style={{ height: 44 }}
                    title="Add comment"
                  >
                    {addingComment ? "Adding..." : "+ Add"}
                  </button>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {attachmentsLoading ? (
                    <div style={{ color: "#9ca3af", padding: 8 }}>Loading…</div>
                  ) : comments.length === 0 ? (
                    <div style={{ color: "#9ca3af", padding: 8 }}>
                      No comments yet.
                    </div>
                  ) : (
                    comments.map((c) => (
                      <div
                        key={c.id || `${c.comment}-${Math.random()}`}
                        style={{
                          padding: 10,
                          borderRadius: 12,
                          border: "1px solid rgba(148,163,184,0.16)",
                          background: "rgba(255,255,255,0.02)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 10,
                            marginBottom: 6,
                          }}
                        >
                          <div style={{ fontSize: 12, color: "#9ca3af" }}>
                            {c.created_at
                              ? formatDateMMDDYY(c.created_at)
                              : c.date
                              ? formatDateMMDDYY(c.date)
                              : ""}
                          </div>

                          <button
                            type="button"
                            className="icon-btn danger"
                            onClick={() => deleteComment(c.id)}
                            title="Delete comment"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>

                        <div style={{ whiteSpace: "pre-wrap", fontSize: 13 }}>
                          {c.comment ?? c.text ?? ""}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Images */}
              <div
                className="card"
                style={{
                  padding: 12,
                  border: "1px solid rgba(148,163,184,0.16)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 10,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <ImageIcon size={16} />
                    <div style={{ fontWeight: 700 }}>
                      Images{" "}
                      <span style={{ color: "#9ca3af", fontWeight: 600 }}>
                        ({images.length})
                      </span>
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    flexWrap: "wrap",
                    marginBottom: 10,
                  }}
                >
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={onPickFiles}
                    style={{ fontSize: 13 }}
                    disabled={attachmentsLoading || uploadingImages}
                  />

                  <button
                    type="button"
                    className="add-button"
                    onClick={uploadImages}
                    disabled={attachmentsLoading || uploadingImages}
                    title="Upload images"
                  >
                    {uploadingImages ? "Uploading..." : "+ Upload"}
                  </button>

                  {selectedFiles?.length ? (
                    <div style={{ fontSize: 12, color: "#9ca3af" }}>
                      {selectedFiles.length} file(s) selected
                    </div>
                  ) : null}
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {attachmentsLoading ? (
                    <div style={{ color: "#9ca3af", padding: 8 }}>Loading…</div>
                  ) : images.length === 0 ? (
                    <div style={{ color: "#9ca3af", padding: 8 }}>
                      No images yet.
                    </div>
                  ) : (
                    images.map((img) => (
                      <div
                        key={img.id || img.imageId || `${img.name}-${Math.random()}`}
                        style={{
                          padding: 10,
                          borderRadius: 12,
                          border: "1px solid rgba(148,163,184,0.16)",
                          background: "rgba(255,255,255,0.02)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 10,
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 13,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: 520,
                            }}
                            title={
                              img.original_name ||
                              img.filename ||
                              img.name ||
                              `Image ${img.id}`
                            }
                          >
                            {img.original_name ||
                              img.filename ||
                              img.name ||
                              `Image ${img.id}`}
                          </div>
                          <div style={{ fontSize: 12, color: "#9ca3af" }}>
                            {img.created_at
                              ? formatDateMMDDYY(img.created_at)
                              : img.date
                              ? formatDateMMDDYY(img.date)
                              : ""}
                          </div>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            alignItems: "center",
                            flexShrink: 0,
                          }}
                        >
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={() => viewImage(img.id || img.imageId)}
                            title="View"
                          >
                            <ImageIcon size={16} />
                          </button>

                          <button
                            type="button"
                            className="icon-btn danger"
                            onClick={() => deleteImage(img.id || img.imageId)}
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div
              style={{
                padding: 12,
                borderTop: "1px solid rgba(148,163,184,0.18)",
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                background: "rgba(255,255,255,0.02)",
              }}
            >
              <button
                type="button"
                className="add-button"
                onClick={closeAttachments}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default WorkOrdersList;
